import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { DataSection, PitchingRankingStats, ResultData, ScoringData, StatType } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import resultsSliceForPitcher from "../../../classificators/resultsSlice/resultsSliceForPitcher.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithNewScoring from "../../../classificators/session/sessionWithNewScoring.js";
import classifiesAsBalk  from '../../../classificators/pitchCall/classifiesAsBalk.js';
import RankingStatCalculatorBase from "../RankingStatCalculatorBase.js";

class NewBalksCalculator extends RankingStatCalculatorBase {
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter,
        balkClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(balkClassifier) !== 'undefined' && typeof(balkClassifier) !== 'function') {
            throw new TypeError('balkClassifier must be an function or undefined');
        }

        super(
            [ PitchingRankingStats.Balks ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithNewScoring,
                satisfiesAll(
                    pitchingScopeContext,
                    resultsSliceForPitcher
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.balkClassifier = balkClassifier || classifiesAsBalk;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const results = slice.getSmallestSlice();

        const stats = this.resultsProcessor.calcStats(
            [StatType.Ratio.key],
            results,
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(ScoringData.PitchCall.key, DataSection.Scoring.key)
            ) ],
            false
        );

        let balks = 0;
        // @see https://www.mlb.com/glossary/standard-stats/balk
        // @see https://baseball.fandom.com/wiki/Balk

        if (!stats || !stats.hasOwnProperty(ScoringData.PitchCall.key) || !stats[ScoringData.PitchCall.key][StatType.Ratio.key]){
            return null;
        }

        for (const result in stats[ScoringData.PitchCall.key][StatType.Ratio.key]) {
            if (!Object.hasOwnProperty.call(stats[ScoringData.PitchCall.key][StatType.Ratio.key], result)) {
                continue;
            }

            if (this.balkClassifier(result)) {
                balks += stats[ScoringData.PitchCall.key][StatType.Ratio.key][result].count;;
            }
        }

        // now build stats
        if (balks > 0) {
            const balksParam = this.supportedParams[0];
            return RankingStat.fromParameter(
                balksParam, this.numberFormatter.format(balks), balks
            );
        }

        return null;
    }
}

export default NewBalksCalculator;